/*
 * The Little Library — product page behaviour
 * File: assets/tll-product.js
 *
 * Three custom elements, loaded only by sections/tll-product.liquid:
 *
 *   <tllp-variants>  the option pickers, and everything that follows from a
 *                    choice: the id the form posts, the price, the Add
 *                    button, the gallery and the address bar.
 *   <tllp-gallery>   a scroll-snap track with thumbnails and arrows.
 *   <tllp-buybar>    the phone bar that appears once Add has scrolled away.
 *
 * Separate from assets/tll-products.js, which snippets/tllp-assets.liquid
 * loads on five pages that have no variant picker between them. Adding to the
 * basket itself is still Dawn's product-form.js.
 *
 * Custom elements upgrade whenever their markup is inserted, so all three
 * keep working after the theme editor re-renders the section.
 */
(() => {
  /* Re-read per call, never cached: someone can change the setting mid-visit. */
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /*
   * Shopify's cookie banner is bottom-anchored, so the phone buy bar has to
   * stay out of its way or the screen ends up with two stacked bars.
   *
   * It has to be asked whether the banner is *showing*, not whether it exists.
   * Shopify injects the element once and then only hides it, so it is in the
   * DOM for the whole visit, answered or not — a CSS :has() test on its
   * presence suppresses the buy bar permanently, for everybody, which is
   * exactly what it used to do here.
   */
  const bannerShowing = () => {
    const el = document.getElementById('shopify-pc__banner');
    return !!el && el.getBoundingClientRect().height > 0;
  };

  /* ---- variant picker ---------------------------------------------------- */

  if (!customElements.get('tllp-variants')) {
    customElements.define(
      'tllp-variants',
      class TllpVariants extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;

          const data = this.querySelector('[data-tllp-variant-data]');
          this.idInput = this.querySelector('[data-tllp-variant-id]');
          this.controls = [...this.querySelectorAll('[data-tllp-value]')];
          if (!data || !this.idInput || !this.controls.length) return;

          try {
            this.variants = JSON.parse(data.textContent);
          } catch (error) {
            return;
          }

          this.root = this.closest('[data-tllpd-root]') || this;
          this.announceEl = this.root.querySelector('[data-tllp-announce]');
          this.unavailableEl = this.root.querySelector('[data-tllp-unavailable]');
          this.optionCount = Number(this.dataset.options || 0);

          this.addEventListener('change', (event) => {
            if (!event.target.closest('[data-tllp-value]')) return;
            this.select();
          });

          // The server already rendered the right state; this only teaches the
          // controls which neighbours are reachable from it.
          this.paintAvailability(this.chosen());
        }

        /* The value chosen for each option, in option order. A radio group
           reports through its checked member; a select through its value. */
        chosen() {
          const values = [];
          for (const control of this.controls) {
            const position = Number(control.dataset.tllpOption || control.closest('[data-tllp-option]')?.dataset.tllpOption || 0);
            if (!position) continue;
            if (control.tagName === 'SELECT') {
              values[position - 1] = control.value;
            } else if (control.checked) {
              values[position - 1] = control.value;
            }
          }
          return values;
        }

        match(values) {
          return this.variants.find((variant) => variant.options.every((option, i) => option === values[i]));
        }

        /*
         * A value is reachable when some variant carries it at its own
         * position, matches the current choice at every other position, and is
         * in stock. Same rule the Liquid uses at render, so the first paint and
         * every later one agree.
         */
        reachable(position, value, values) {
          return this.variants.some((variant) => {
            if (!variant.available) return false;
            return variant.options.every((option, i) => (i === position - 1 ? option === value : option === values[i]));
          });
        }

        paintAvailability(values) {
          for (const control of this.controls) {
            const position = Number(control.dataset.tllpOption || control.closest('[data-tllp-option]')?.dataset.tllpOption || 0);
            if (!position) continue;

            if (control.tagName === 'SELECT') {
              for (const option of control.options) {
                option.toggleAttribute('data-unavailable', !this.reachable(position, option.value, values));
              }
              continue;
            }
            // Marked, never disabled: a disabled radio cannot be focused, and
            // the value has to stay reachable so the shopper can find out why.
            control.toggleAttribute('data-unavailable', !this.reachable(position, control.value, values));
          }
        }

        select() {
          const values = this.chosen();
          this.paintAvailability(values);

          const variant = this.match(values);
          if (!variant) {
            this.noSuchVariant(values);
            return;
          }

          if (this.unavailableEl) this.unavailableEl.hidden = true;
          this.idInput.value = variant.id;

          this.paintPrice(variant.id);
          this.paintButtons(variant);
          this.syncUrl(variant.id);

          if (variant.media) {
            this.root.querySelector('tllp-gallery')?.showMedia(variant.media);
          }

          this.announce(`${variant.title}. ${this.priceText()}`);
          this.dispatchEvent(new CustomEvent('tllp:variant', { bubbles: true, detail: { variant } }));
        }

        /*
         * The chosen combination does not exist. Keep the choice showing, keep
         * the last real id on the form, and say so — quietly jumping to a
         * different variant is how people order the wrong book.
         */
        noSuchVariant(values) {
          this.paintButtons(null);
          const wanted = values.filter(Boolean).join(' with ');
          if (this.unavailableEl) {
            this.unavailableEl.textContent = `${wanted} isn't available.`;
            this.unavailableEl.hidden = false;
          }
          this.announce(`${wanted} isn't available.`);
        }

        /* Prices come from server-rendered templates, so money formatting,
           sale styling and "save" copy stay in the shop's own currency. */
        paintPrice(id) {
          const source = this.querySelector(`template[data-tllp-price="${CSS.escape(String(id))}"]`);
          if (!source) return;
          for (const target of this.root.querySelectorAll('[data-tllp-price-target]')) {
            target.innerHTML = source.innerHTML;
          }
        }

        priceText() {
          return this.root.querySelector('[data-tllp-price-target] .tllp-price__now')?.textContent.trim() || '';
        }

        paintButtons(variant) {
          const available = !!variant && variant.available;
          for (const button of this.root.querySelectorAll('[data-tllp-add]')) {
            button.disabled = !available;
            // Only the label span is written. Replacing the button's text
            // would take the loading spinner and the "Added" tick with it.
            const label = button.querySelector('[data-tllp-add-label]');
            if (label) {
              label.textContent = available ? button.dataset.labelAdd || 'Add to basket' : button.dataset.labelSoldOut || 'Sold out';
            }
          }
        }

        /* replaceState, not pushState: Back should leave the product, not walk
           the shopper back through every option they tried. */
        syncUrl(id) {
          if (!window.history?.replaceState) return;
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', id);
            window.history.replaceState(window.history.state, '', url);
          } catch (error) {
            /* A URL the browser will not parse is not worth a broken page. */
          }
        }

        announce(message) {
          if (!this.announceEl) return;
          this.announceEl.textContent = '';
          requestAnimationFrame(() => {
            this.announceEl.textContent = message;
          });
        }
      }
    );
  }

  /* ---- gallery ------------------------------------------------------------ */

  if (!customElements.get('tllp-gallery')) {
    customElements.define(
      'tllp-gallery',
      class TllpGallery extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;

          this.track = this.querySelector('[data-tllp-track]');
          this.slides = [...this.querySelectorAll('[data-tllp-slide]')];
          if (!this.track || this.slides.length < 2) return;

          this.thumbs = [...this.querySelectorAll('[data-tllp-thumb]')];
          this.current = this.querySelector('[data-tllp-current]');
          this.prev = this.querySelector('[data-tllp-prev]');
          this.next = this.querySelector('[data-tllp-next]');
          this.index = 0;

          this.addEventListener('click', (event) => {
            const thumb = event.target.closest('[data-tllp-thumb]');
            if (thumb) {
              this.go(Number(thumb.dataset.tllpThumb));
              return;
            }
            if (event.target.closest('[data-tllp-prev]')) this.go(this.index - 1);
            if (event.target.closest('[data-tllp-next]')) this.go(this.index + 1);
          });

          this.track.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
          });

          // Swipes and trackpad scrolls move the counter too, not just the
          // buttons.
          this.observer = new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                const index = this.slides.indexOf(entry.target);
                if (index > -1) this.update(index);
              }
            },
            { root: this.track, threshold: 0.6 }
          );
          this.slides.forEach((slide) => this.observer.observe(slide));

          this.update(0);
        }

        disconnectedCallback() {
          this.observer?.disconnect();
        }

        go(index, instant) {
          const target = Math.max(0, Math.min(this.slides.length - 1, index));
          // The track is the scroll container, so offsetLeft is measured from
          // its own start and is unaffected by how far it is already scrolled.
          this.track.scrollTo({
            left: this.slides[target].offsetLeft,
            behavior: instant || reduceMotion() ? 'auto' : 'smooth',
          });
          this.update(target);
        }

        update(index) {
          this.index = index;
          if (this.current) this.current.textContent = String(index + 1);
          this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
          if (this.prev) this.prev.disabled = index === 0;
          if (this.next) this.next.disabled = index === this.slides.length - 1;
        }

        /* Called by the variant picker. A variant with no picture of its own
           leaves the gallery where it is, which is what people expect when
           the option they changed shares a photo. */
        showMedia(id) {
          const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
          if (index > -1) this.go(index);
        }
      }
    );
  }

  /* ---- sticky buy bar ------------------------------------------------------ */

  if (!customElements.get('tllp-buybar')) {
    customElements.define(
      'tllp-buybar',
      class TllpBuyBar extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;

          this.bar = this.querySelector('[data-tllpd-bar]');
          const root = this.closest('[data-tllpd-root]') || document;
          this.anchor = root.querySelector('[data-tllp-add-main]');
          this.mirror = this.querySelector('[data-tllp-add]');
          if (!this.bar || !this.anchor || !('IntersectionObserver' in window)) return;

          /*
           * Show only once the real Add button has left the TOP of the screen.
           * The basket's pinned bar reads the opposite way — it hides when its
           * target is visible or above — because there the target is the last
           * thing on the page. Here it is halfway up, and a bar that appeared
           * before the shopper had even reached the button would be nagging.
           */
          /*
           * The answer is recomputed from the button's own box every time,
           * never cached from an observer entry. An IntersectionObserver only
           * reports when the ratio crosses a threshold, and this button can go
           * from below the fold to above it in one jump — a restored scroll
           * position, an anchor link, a long flick — without ever intersecting.
           * The ratio stays 0 throughout, no entry is queued, and a cached flag
           * would still be saying the button is on screen.
           */
          this.paint = () => {
            const gone = this.anchor.getBoundingClientRect().bottom <= 0;
            this.bar.toggleAttribute('data-hidden', !gone || bannerShowing());
          };

          /* The observer is the cheap wake-up for the ordinary case; the
             listener is the backstop for the jumps it does not report. */
          this.observer = new IntersectionObserver(() => this.paint());
          this.observer.observe(this.anchor);

          this.onScroll = () => {
            if (this.frame) return;
            this.frame = requestAnimationFrame(() => {
              this.frame = 0;
              this.paint();
            });
          };
          addEventListener('scroll', this.onScroll, { passive: true });
          addEventListener('resize', this.onScroll, { passive: true });
          this.paint();

          /*
           * The banner is Shopify's, injected after this script runs and
           * dismissed without a page load, so the bar has to be told when it
           * goes. Watching its attributes covers the dismissal; watching the
           * body for one childList change covers its arrival.
           */
          const watchBanner = () => {
            const el = document.getElementById('shopify-pc__banner');
            if (!el || this.bannerWatcher) return !!el;
            this.bannerWatcher = new MutationObserver(() => this.paint());
            this.bannerWatcher.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
            this.paint();
            return true;
          };

          if (!watchBanner()) {
            this.arrivalWatcher = new MutationObserver(() => {
              if (watchBanner()) {
                this.arrivalWatcher.disconnect();
                this.arrivalWatcher = null;
              }
            });
            this.arrivalWatcher.observe(document.body, { childList: true });
          }

          /*
           * product-form.js only knows about the button inside <product-form>,
           * so the bar's copy would stay idle through the whole fetch. Mirror
           * the loading state across.
           */
          if (this.mirror) {
            this.classWatcher = new MutationObserver(() => {
              this.mirror.classList.toggle('loading', this.anchor.classList.contains('loading'));
              const from = this.anchor.querySelector('.loading__spinner');
              const to = this.mirror.querySelector('.loading__spinner');
              if (from && to) to.classList.toggle('hidden', from.classList.contains('hidden'));
            });
            this.classWatcher.observe(this.anchor, { attributes: true, attributeFilter: ['class'] });
            this.spinnerWatcher = new MutationObserver(() => {
              const from = this.anchor.querySelector('.loading__spinner');
              const to = this.mirror.querySelector('.loading__spinner');
              if (from && to) to.classList.toggle('hidden', from.classList.contains('hidden'));
            });
            const spinner = this.anchor.querySelector('.loading__spinner');
            if (spinner) this.spinnerWatcher.observe(spinner, { attributes: true, attributeFilter: ['class'] });
          }
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          this.classWatcher?.disconnect();
          this.spinnerWatcher?.disconnect();
          this.bannerWatcher?.disconnect();
          this.arrivalWatcher?.disconnect();
          if (this.onScroll) {
            removeEventListener('scroll', this.onScroll);
            removeEventListener('resize', this.onScroll);
          }
          if (this.frame) cancelAnimationFrame(this.frame);
        }
      }
    );
  }
})();
